import type { OutreachCampaign, PackageTier } from '../types/outreach'

export const packageTiers: PackageTier[] = [
  {
    id: 'bronze',
    label: 'Bronze',
    priceAed: 120_000,
    channelCount: 4,
    impressionGuarantee: 750_000,
    inclusions: [
      'Listing on the UAE national events portal',
      'Emirate tourism board event calendar',
      'One official social post per platform',
      'Standard post-campaign reach report'
    ]
  },
  {
    id: 'silver',
    label: 'Silver',
    priceAed: 340_000,
    channelCount: 8,
    impressionGuarantee: 2_400_000,
    inclusions: [
      'Everything in Bronze',
      'Featured placement on two government portals',
      'Regional tourism board newsletter inclusion',
      'Three-week official social campaign',
      'Media office press distribution'
    ]
  },
  {
    id: 'gold',
    label: 'Gold',
    priceAed: 780_000,
    channelCount: 14,
    impressionGuarantee: 6_800_000,
    inclusions: [
      'Everything in Silver',
      'Airline inflight and lounge placement',
      'Embassy network cultural bulletin',
      'Homepage takeover on the national portal',
      'Influencer and creator programme',
      'Mid-campaign optimisation review'
    ]
  },
  {
    id: 'platinum',
    label: 'Platinum',
    priceAed: 1_650_000,
    channelCount: 22,
    impressionGuarantee: 18_000_000,
    inclusions: [
      'Everything in Gold',
      'Full ecosystem activation across all partners',
      'Out-of-home network across four emirates',
      'International broadcast partner integration',
      'Dedicated SDU outreach producer',
      'Weekly performance reviews with AI analysis'
    ]
  }
]

export const campaigns: OutreachCampaign[] = [
  {
    id: 'oc-1',
    applicationId: 'app-9',
    eventName: 'Abu Dhabi Festival of Classical Arts 2026',
    entityName: 'Department of Culture & Tourism — Abu Dhabi',
    emirate: 'Abu Dhabi',
    tier: 'gold',
    startsOn: '2026-07-02',
    endsOn: '2026-11-20',
    impressions: 4_180_000,
    engagements: 214_000,
    referrals: 68_400,
    placements: [
      { id: 'pl-1', partner: 'u.ae National Portal', network: 'Government portals', state: 'live', scheduledOn: '2026-07-06', impressions: 1_120_000, target: 900_000 },
      { id: 'pl-2', partner: 'Experience Abu Dhabi', network: 'Tourism boards', state: 'live', scheduledOn: '2026-07-08', impressions: 940_000, target: 1_000_000 },
      { id: 'pl-3', partner: 'Etihad Airways inflight', network: 'Airlines', state: 'live', scheduledOn: '2026-07-20', impressions: 860_000, target: 700_000 },
      { id: 'pl-4', partner: 'UAE Embassy Cultural Bulletin', network: 'Embassy network', state: 'live', scheduledOn: '2026-08-01', impressions: 384_000, target: 600_000 },
      { id: 'pl-5', partner: 'UAE Government Media Office', network: 'Media offices', state: 'delivered', scheduledOn: '2026-07-14', impressions: 512_000, target: 480_000 },
      { id: 'pl-6', partner: 'Official UAE social accounts', network: 'Social platforms', state: 'live', scheduledOn: '2026-07-10', impressions: 364_000, target: 620_000 },
      { id: 'pl-7', partner: 'Visit Dubai cross-promotion', network: 'Tourism boards', state: 'scheduled', scheduledOn: '2026-09-05', impressions: 0, target: 700_000 },
      { id: 'pl-8', partner: 'Creator programme — culture cohort', network: 'Social platforms', state: 'queued', scheduledOn: '2026-09-22', impressions: 0, target: 800_000 }
    ],
    calendar: [
      { id: 'ce-1', day: 3, label: 'National portal feature goes live', partner: 'u.ae National Portal', state: 'live' },
      { id: 'ce-2', day: 5, label: 'Tourism board calendar listing', partner: 'Experience Abu Dhabi', state: 'live' },
      { id: 'ce-3', day: 9, label: 'Press distribution', partner: 'UAE Government Media Office', state: 'delivered' },
      { id: 'ce-4', day: 14, label: 'Inflight content window opens', partner: 'Etihad Airways', state: 'live' },
      { id: 'ce-5', day: 18, label: 'Embassy cultural bulletin', partner: 'Embassy network', state: 'live' },
      { id: 'ce-6', day: 22, label: 'Creator programme briefing', partner: 'Creator cohort', state: 'queued' },
      { id: 'ce-7', day: 26, label: 'Cross-emirate promotion begins', partner: 'Visit Dubai', state: 'scheduled' }
    ],
    weekly: [
      { week: 'W1', impressions: 420_000, engagements: 18_400 },
      { week: 'W2', impressions: 610_000, engagements: 27_800 },
      { week: 'W3', impressions: 720_000, engagements: 36_200 },
      { week: 'W4', impressions: 690_000, engagements: 38_900 },
      { week: 'W5', impressions: 810_000, engagements: 44_100 },
      { week: 'W6', impressions: 930_000, engagements: 48_600 }
    ],
    geography: [
      { market: 'UAE', share: 38 },
      { market: 'GCC', share: 21 },
      { market: 'Europe', share: 18 },
      { market: 'South Asia', share: 13 },
      { market: 'Americas', share: 6 },
      { market: 'East Asia', share: 4 }
    ],
    aiNote:
      'Campaign is at 61% of the 6.8M impression guarantee with 44% of the window remaining — on track overall. The embassy network is the only material laggard at 64% of its target; airline inflight is over-delivering by 23% and can absorb reallocated inventory without additional cost.'
  },
  {
    id: 'oc-2',
    applicationId: 'app-10',
    eventName: 'Dubai Esports & Games Festival 2026',
    entityName: 'Dubai World Trade Centre Authority',
    emirate: 'Dubai',
    tier: 'platinum',
    startsOn: '2026-06-10',
    endsOn: '2026-10-01',
    impressions: 14_600_000,
    engagements: 1_180_000,
    referrals: 402_000,
    placements: [
      { id: 'pl-9', partner: 'u.ae National Portal', network: 'Government portals', state: 'live', scheduledOn: '2026-06-12', impressions: 2_400_000, target: 2_000_000 },
      { id: 'pl-10', partner: 'Visit Dubai', network: 'Tourism boards', state: 'live', scheduledOn: '2026-06-14', impressions: 3_100_000, target: 2_800_000 },
      { id: 'pl-11', partner: 'Emirates inflight & lounges', network: 'Airlines', state: 'live', scheduledOn: '2026-06-20', impressions: 2_800_000, target: 2_600_000 },
      { id: 'pl-12', partner: 'flydubai network', network: 'Airlines', state: 'live', scheduledOn: '2026-06-25', impressions: 980_000, target: 900_000 },
      { id: 'pl-13', partner: 'Embassy network — digital economy bulletin', network: 'Embassy network', state: 'live', scheduledOn: '2026-07-01', impressions: 1_240_000, target: 1_200_000 },
      { id: 'pl-14', partner: 'Out-of-home — four emirates', network: 'Out of home', state: 'live', scheduledOn: '2026-07-08', impressions: 2_600_000, target: 3_000_000 },
      { id: 'pl-15', partner: 'International broadcast partner', network: 'Broadcast', state: 'scheduled', scheduledOn: '2026-09-10', impressions: 0, target: 3_400_000 },
      { id: 'pl-16', partner: 'Official UAE social accounts', network: 'Social platforms', state: 'live', scheduledOn: '2026-06-16', impressions: 1_480_000, target: 1_400_000 }
    ],
    calendar: [
      { id: 'ce-8', day: 2, label: 'National portal homepage takeover', partner: 'u.ae National Portal', state: 'live' },
      { id: 'ce-9', day: 4, label: 'Visit Dubai hero placement', partner: 'Visit Dubai', state: 'live' },
      { id: 'ce-10', day: 10, label: 'Airline inflight window opens', partner: 'Emirates', state: 'live' },
      { id: 'ce-11', day: 16, label: 'Out-of-home network live', partner: 'Four emirates OOH', state: 'live' },
      { id: 'ce-12', day: 21, label: 'Embassy bulletin distribution', partner: 'Embassy network', state: 'delivered' },
      { id: 'ce-13', day: 27, label: 'Broadcast partner integration', partner: 'International broadcast', state: 'scheduled' }
    ],
    weekly: [
      { week: 'W1', impressions: 1_640_000, engagements: 118_000 },
      { week: 'W2', impressions: 2_180_000, engagements: 164_000 },
      { week: 'W3', impressions: 2_460_000, engagements: 198_000 },
      { week: 'W4', impressions: 2_720_000, engagements: 214_000 },
      { week: 'W5', impressions: 2_800_000, engagements: 232_000 },
      { week: 'W6', impressions: 2_800_000, engagements: 254_000 }
    ],
    geography: [
      { market: 'UAE', share: 29 },
      { market: 'GCC', share: 17 },
      { market: 'East Asia', share: 19 },
      { market: 'Europe', share: 16 },
      { market: 'South Asia', share: 12 },
      { market: 'Americas', share: 7 }
    ],
    aiNote:
      'Platinum campaign is running 8% ahead of the 18M guarantee trajectory. Out-of-home is the only channel below plan at 87% of target; the broadcast integration in week 27 is forecast to close the gap. Overseas share at 71% exceeds the tier assumption of 60%.'
  },
  {
    id: 'oc-3',
    applicationId: 'app-13',
    eventName: 'Ajman Heritage Days 2026',
    entityName: 'Ajman Chamber of Commerce & Industry',
    emirate: 'Ajman',
    tier: 'silver',
    startsOn: '2026-07-25',
    endsOn: '2026-10-10',
    impressions: 1_060_000,
    engagements: 42_800,
    referrals: 11_600,
    placements: [
      { id: 'pl-17', partner: 'u.ae National Portal', network: 'Government portals', state: 'live', scheduledOn: '2026-07-27', impressions: 486_000, target: 400_000 },
      { id: 'pl-18', partner: 'Ajman Tourism', network: 'Tourism boards', state: 'live', scheduledOn: '2026-07-29', impressions: 232_000, target: 400_000 },
      { id: 'pl-19', partner: 'UAE Government Media Office', network: 'Media offices', state: 'delivered', scheduledOn: '2026-08-03', impressions: 184_000, target: 200_000 },
      { id: 'pl-20', partner: 'Official UAE social accounts', network: 'Social platforms', state: 'live', scheduledOn: '2026-08-01', impressions: 158_000, target: 300_000 },
      { id: 'pl-21', partner: 'Northern emirates newsletter', network: 'Tourism boards', state: 'scheduled', scheduledOn: '2026-09-02', impressions: 0, target: 300_000 },
      { id: 'pl-22', partner: 'Heritage creator cohort', network: 'Social platforms', state: 'queued', scheduledOn: '2026-09-14', impressions: 0, target: 400_000 }
    ],
    calendar: [
      { id: 'ce-14', day: 2, label: 'National portal listing', partner: 'u.ae National Portal', state: 'live' },
      { id: 'ce-15', day: 4, label: 'Tourism board calendar', partner: 'Ajman Tourism', state: 'live' },
      { id: 'ce-16', day: 9, label: 'Press distribution', partner: 'UAE Government Media Office', state: 'delivered' },
      { id: 'ce-17', day: 17, label: 'Newsletter inclusion', partner: 'Northern emirates', state: 'scheduled' },
      { id: 'ce-18', day: 24, label: 'Creator cohort activation', partner: 'Heritage cohort', state: 'queued' }
    ],
    weekly: [
      { week: 'W1', impressions: 210_000, engagements: 7_200 },
      { week: 'W2', impressions: 268_000, engagements: 9_800 },
      { week: 'W3', impressions: 246_000, engagements: 11_400 },
      { week: 'W4', impressions: 336_000, engagements: 14_400 }
    ],
    geography: [
      { market: 'UAE', share: 62 },
      { market: 'GCC', share: 19 },
      { market: 'South Asia', share: 11 },
      { market: 'Europe', share: 5 },
      { market: 'Other', share: 3 }
    ],
    aiNote:
      'Campaign is at 44% of the 2.4M guarantee with 52% of the window elapsed — marginally behind. Tourism board placements are the cause at 58% of target, while national portal inventory is over-delivering by 22%. Reallocating two portal slots is forecast to recover the shortfall within three weeks.'
  }
]

export function campaignById(id?: string) {
  if (!id) return undefined
  return campaigns.find((c) => c.id === id)
}

export function tierById(id: string) {
  return packageTiers.find((t) => t.id === id)
}
